import React, { useCallback, useEffect, useRef } from "react";
import { FormHelperText } from "@mui/material";
import { DocumentUpload } from "@components/FileUpload/FileUpload";
import { useFileUploadProfiles } from "@components/FileUpload/FileUpload.constants";
import SingleFile from "@components/FileUpload/SingleFile";
import { useUploadStore, type UploadEntry } from "@components/FileUpload/uploadStore";
import { useFormattedReference } from "@hooks/useFormattedReference";
import { uploadFileToAzureBlob } from "@services/azureDataApi";
import { REFERENCE_DATA_TYPES } from "@services/referenceDataApi";
import { SasTokenPrefix, SasTokenPrivilege } from "@services/user/types";
import { getSasToken } from "@services/userApi";
import type { FromToLocation } from "@services/vessel-call/types";
import { useMutation } from "@tanstack/react-query";
import { runTransaction } from "@telemetry/runTransaction";
import { useTranslation } from "react-i18next";
import type { ChangeStatusDrawerInfo } from "../../hoc/withDrawers";
import { usePortServicesFlow } from "../context/PortServicesFlowContext";

const LANG_BASE = "vessel-call.port-services";

interface GarbageDisposalDocumentProps {
  serviceDetails: ChangeStatusDrawerInfo;
}

const GarbageDisposalDocument = ({
  serviceDetails: { location: disposalLocation },
}: GarbageDisposalDocumentProps) => {
  const EMPTY_UPLOAD_ENTRIES: UploadEntry[] = [];
  const { t } = useTranslation();
  const { errors, setErrors, setGarbageDisposalDocument } = usePortServicesFlow();

  const garbageDisposalDocumentUploadSignatureRef = useRef<string | null>(null);
  const setFilesForKey = useUploadStore((s) => s.setFilesForKey);
  const clearFilesForKey = useUploadStore((s) => s.clearFilesForKey);

  const garbageDisposalDocument = useUploadStore(
    (s) => s.filesByKey.garbageDisposalDocument ?? EMPTY_UPLOAD_ENTRIES,
  );

  const fileProfiles = useFileUploadProfiles();
  const internalProfile = fileProfiles.sideDrawerApprovalFile;
  const allowedInternalExtensions = internalProfile.format.map((ext) =>
    ext.startsWith(".") ? ext.toLowerCase() : `.${ext.toLowerCase()}`,
  );

  const setGarbageDisposalDocumentFiles = useCallback(
    (files: UploadEntry[]) => {
      if (!files.length) {
        clearFilesForKey("garbageDisposalDocument");
        return;
      }
      setFilesForKey("garbageDisposalDocument", files);
    },
    [clearFilesForKey, setFilesForKey],
  );

  const clearGarbageDisposalDocumentErrors = useCallback(() => {
    setErrors((prev) => ({
      ...prev,
      files: undefined,
      garbageDisposalDocument: undefined,
    }));
  }, [setErrors]);

  const getFileSignature = (entry: UploadEntry | undefined) => {
    if (!(entry instanceof File)) return null;
    return `${entry.name}-${entry.lastModified}-${entry.size}`;
  };

  const triggerUpload = useCallback(
    (
      entry: UploadEntry | undefined,
      handler: ((file: File) => void) | undefined,
      tracker: React.RefObject<string | null>,
    ) => {
      if (!handler) return;
      if (!(entry instanceof File)) return;

      const signature = getFileSignature(entry);
      if (!signature) return;

      if (tracker.current === signature) return;

      tracker.current = signature;
      handler(entry);
    },
    [],
  );

  const removeGarbageDisposalDocument = () => {
    clearFilesForKey("garbageDisposalDocument");
    setGarbageDisposalDocument({ documentUrl: "", documentName: "", fileType: "" });
  };

  const documentUploadMutation = useMutation({
    mutationFn: ({ file, documentType }: { file: File; documentType: string }) =>
      runTransaction(
        {
          module: "vessel_call",
          screen: "PortServiceDocumentUpload",
          action: "upload_port_service_document",
          entityType: "port_service_document",
        },
        async (transactionId) => {
          const payloadForSasToken = {
            prefix: SasTokenPrefix.PORT_SERVICES,
            contentType: file.type || null,
            privilege: SasTokenPrivilege.WRITE,
            blobUrl: null,
          };

          const { blobUrl, sasToken } = await getSasToken(
            payloadForSasToken,
            ...(transactionId ? [transactionId] : []),
          );
          const [uploadResult] = await uploadFileToAzureBlob({
            file,
            blobUrl,
            sasToken,
            certificateType: documentType,
          });

          const documentUrl = uploadResult?.documentUrl ?? blobUrl;
          setGarbageDisposalDocument({
            documentUrl: documentUrl,
            documentName: file?.name,
            fileType: documentType,
          });
          return { documentUrl, documentType };
        },
      ),
    onSuccess: clearGarbageDisposalDocumentErrors,
  });

  const handleDocumentUpload = (file: File, documentType: string) => {
    documentUploadMutation.mutate({ file, documentType });
  };

  const handleFileUpload = ({
    newFiles,
    setUploadErrors,
    setUploadFiles,
    onUploadDocument,
    uploadTracker,
  }: {
    newFiles: File[];
    setUploadErrors: (value: { garbageDisposalDocument?: string; files?: string }) => void;
    setUploadFiles: (files: UploadEntry[]) => void;
    onUploadDocument?: (file: File) => void;
    uploadTracker: React.RefObject<string | null>;
  }) => {
    if (newFiles.length > 1) {
      setUploadErrors({ garbageDisposalDocument: t("upload.one-document") });
      return;
    }

    const file = newFiles[0];
    if (!file) {
      return;
    }

    setUploadErrors({ files: undefined, garbageDisposalDocument: undefined });
    setUploadFiles([file]);
    triggerUpload(file, onUploadDocument, uploadTracker);
  };

  useEffect(() => {
    return () => {
      garbageDisposalDocumentUploadSignatureRef.current = null;
      removeGarbageDisposalDocument();
    };
  }, []);

  const terminals = useFormattedReference<FromToLocation>(
    REFERENCE_DATA_TYPES.FROM_TO_LOCATIONS,
    "bff-gatepass",
  ).options.filter((location) => location.location === "BERTH");

  const required = terminals.some((location) => location.fromToLocationCode === disposalLocation);

  return (
    <div className="flex flex-col gap-2 mb-4" data-testid="port-services-garbage-disposal-document">
      <DocumentUpload
        label={t(`${LANG_BASE}.fields.garbage-disposal-document`)}
        required={required}
        testId="port-services-garbage-disposal-document-upload"
        onUpload={(newFiles) =>
          handleFileUpload({
            newFiles,
            setUploadErrors: (fileErrors) =>
              setErrors((prev) => ({
                ...prev,
                ...fileErrors,
              })),
            setUploadFiles: setGarbageDisposalDocumentFiles,
            onUploadDocument: (file) => handleDocumentUpload(file, "GARBAGE_DISPOSAL"),
            uploadTracker: garbageDisposalDocumentUploadSignatureRef,
          })
        }
        name="garbageDisposalDocument"
        conditions={internalProfile}
        files={garbageDisposalDocument}
        setFiles={(next) => setFilesForKey("garbageDisposalDocument", next)}
        maxFiles={1}
        allowedExtensions={allowedInternalExtensions}
        uploadDescription={t("upload.dragOrDropDescription")}
      />
      {errors.garbageDisposalDocument && (
        <FormHelperText sx={{ color: "error.main", m: 0 }}>
          {errors.garbageDisposalDocument}
        </FormHelperText>
      )}
      {garbageDisposalDocument[0] && (
        <SingleFile
          file={garbageDisposalDocument[0]}
          testId="port-services-garbage-disposal-document"
          fieldName="garbageDisposalDocument"
          currentFileIndex={0}
          setFiles={() => {
            setErrors({ ...errors, files: undefined });
            setGarbageDisposalDocumentFiles([]);
            garbageDisposalDocumentUploadSignatureRef.current = null;
          }}
          removeFn={removeGarbageDisposalDocument}
        />
      )}
    </div>
  );
};

export default GarbageDisposalDocument;
